import { getRegionSettings } from './getRegionSettings.ts'
import { VARIANT_COPY_KEYS } from './keyVariants.ts'
export const LAND_SUPPORTED_LANGUAGES = ['az', 'en', 'ky', 'kz', 'ru', 'tr', 'uz'] as const
export const SUPPORTED_GEO = ['KZ', 'AZ', 'UZ', 'KG'] as const
export type SupportedLanguage = (typeof LAND_SUPPORTED_LANGUAGES)[number]
export type SupportedGeo = (typeof SUPPORTED_GEO)[number]

export type ProjectInfoEntry = {
  project_id: number
  project_uuid: string
  currencies: string[]
  geo_main: string[]
  geo_main_alpha_3: string[]
  locales: string[]
  cda: string
}

export type IpInfoResponse = {
  alpha_3?: string
  alpha_2?: string
}

export type DecorativeAsset = {
  file: string
  className: string
}

export type ProjectGeoConfig = {
  languages: string[]
  currency: string
} & Pick<ProjectInfoEntry, 'cda' | 'project_id' | 'project_uuid'>

type MainElementConfig = {
  mobile: string
  tablet: string
  desktop: string
  className: string
}

type PhoneConfig = {
  code: SupportedGeo
  dial: string
  maxLength: number
  placeholder: string
  flagSrc: string
  flagAlt: string
}
type LayoutConfig = {
  mainElement1: MainElementConfig
  mainElement2: MainElementConfig
  decorElements: {
    mobile: DecorativeAsset[]
    tablet: DecorativeAsset[]
    desktop: DecorativeAsset[]
  }
  currency: string
}

export type GeoConfig = {
  id: SupportedGeo
  phone: PhoneConfig
  layout: LayoutConfig
}

export type GeoRuntimeSettings = {
  defaultLanguage: SupportedLanguage
  supportedLanguages: SupportedLanguage[]
  countryOfResidence: string
  currency: string
}

export type GeoRuntimeConfig = GeoConfig & GeoRuntimeSettings

export const DEFAULT_GEO: SupportedGeo = SUPPORTED_GEO[0]
export const GEO_STORAGE_KEY = 'preferredGeo'
export const LANGUAGE_STORAGE_KEY = 'preferredLanguage' // already used in index.js
export const GEO_QUERY_KEY = 'geo'

export const LANGUAGE_TO_GEO: Partial<Record<SupportedLanguage, SupportedGeo>> = {
  az: 'AZ',
  tr: 'AZ',
  ky: 'KG',
  kz: 'KZ',
  uz: 'UZ',
}

const LANGUAGE_ALIASES: Record<string, SupportedLanguage> = {
  kk: 'kz',
}

export const LANGUAGE_META: Record<SupportedLanguage, { flag: string; label: string }> = {
  az: { flag: '/flags/az.svg', label: 'AZ' },
  en: { flag: '/flags/en.svg', label: 'EN' },
  ky: { flag: '/flags/kg.svg', label: 'KG' },
  kz: { flag: '/flags/kz.svg', label: 'KZ' },
  ru: { flag: '/flags/ru.svg', label: 'RU' },
  tr: { flag: '/flags/tr.svg', label: 'TR' },
  uz: { flag: '/flags/uz.svg', label: 'UZ' },
}

export const GEO_META: Record<SupportedGeo, { flag: string; label: string }> = {
  AZ: { flag: '/flags/az.svg', label: 'AZ' },
  KG: { flag: '/flags/kg.svg', label: 'KG' },
  KZ: { flag: '/flags/kz.svg', label: 'KZ' },
  UZ: { flag: '/flags/uz.svg', label: 'UZ' },
}

export const getCommonAssetPath = (file: string) => `/assets/img/${file}`

export const getGeoAssetPath = (geo: SupportedGeo, file: string) =>
  `/assets/img/${geo.toLowerCase()}/${file}`

export const getGeoCopyKey = (geo: SupportedGeo, language: SupportedLanguage, key: string) =>
  VARIANT_COPY_KEYS[language]?.[geo]?.includes(key) ? `variants.${geo}.${key}` : key

export const GEO_RUNTIME_FALLBACKS: Record<SupportedGeo, GeoRuntimeSettings> = {
  AZ: {
    defaultLanguage: 'az',
    supportedLanguages: ['ru', 'az', 'tr', 'en'],
    countryOfResidence: 'AZE',
    currency: 'AZN',
  },
  KG: {
    defaultLanguage: 'ru',
    supportedLanguages: ['ru', 'ky', 'en'],
    countryOfResidence: 'KGZ',
    currency: 'KGS',
  },
  KZ: {
    defaultLanguage: 'kz',
    supportedLanguages: ['ru', 'kz', 'en'],
    countryOfResidence: 'KAZ',
    currency: 'KZT',
  },
  UZ: {
    defaultLanguage: 'uz',
    supportedLanguages: ['ru', 'uz', 'en'],
    countryOfResidence: 'UZB',
    currency: 'UZS',
  },
}

export const normalizeGeo = (value: string | null | undefined): SupportedGeo | null => {
  if (!value) return null

  const normalized = value.trim().toUpperCase()

  return SUPPORTED_GEO.some((geo) => geo === normalized) ? (normalized as SupportedGeo) : null
}

export const normalizeLanguage = (value: string | null | undefined): SupportedLanguage | null => {
  if (!value) return null

  const normalized = value.trim().toLowerCase()
  const shortCode = normalized.split('-')[0]
  const aliased = LANGUAGE_ALIASES[shortCode] ?? shortCode

  return LAND_SUPPORTED_LANGUAGES.includes(aliased) ? (aliased as SupportedLanguage) : null
}

export const GEO_CONFIG: Record<SupportedGeo, GeoConfig> = {
  AZ: {
    id: 'AZ',
    phone: {
      code: 'AZ',
      dial: '994',
      maxLength: 13,
      placeholder: '+994 XX XXX XX XX',
      flagSrc: getGeoAssetPath('AZ', 'phone.svg'),
      flagAlt: 'AZ flag',
    },
    layout: {
      mainElement1: {
        mobile: 'girl-1.webp',
        tablet: 'girl-1.webp',
        desktop: 'girl-1.webp',
        className: 'main__girl-1',
      },
      mainElement2: {
        mobile: 'girl-2.webp',
        tablet: 'girl-2.webp',
        desktop: 'girl-2.webp',
        className: 'main__girl-2',
      },
      decorElements: {
        mobile: [
          {
            file: 'chips.webp',
            className: 'estop absolute main__chips-3 flyingDown',
          },
        ],

        tablet: [
          {
            file: 'chips.webp',
            className: 'estop absolute main__chips-3 flyingDown',
          },

          {
            file: 'effect-2.webp',
            className: 'estop absolute main__effetct-2',
          },
        ],

        desktop: [
          {
            file: 'neon3.webp',
            className: 'estop main__neon fadeInPulse',
          },
          {
            file: 'effect-12.png',
            className: 'estop absolute main__effetct-1 fadeInPulse',
          },
          {
            file: 'effect-22.webp',
            className: 'estop absolute main__effetct-2 fadeInPulse',
          },

          {
            file: 'chips-03.webp',
            className: 'estop absolute main__chips-0 flyingDown',
          },

          {
            file: 'chips-13.webp',
            className: 'estop absolute main__chips-1 flyingDown',
          },

          {
            file: 'chips-23.webp',
            className: 'estop absolute main__chips-2 flyingDown',
          },

          {
            file: 'chips-33.webp',
            className: 'estop absolute main__chips-3 flyingDown',
          },

          {
            file: 'chips-4.webp',
            className: 'estop absolute main__chips-4 flyingDown',
          },

          {
            file: 'chips-5.webp',
            className: 'estop absolute main__chips-5 flyingDown',
          },

          {
            file: 'chips-6.webp',
            className: 'estop absolute main__chips-6 flyingDown',
          },

          {
            file: 'cloud-1.webp',
            className: 'estop absolute main__cloud-1 fadeInPulse',
          },

          {
            file: 'cloud-2.webp',
            className: 'estop absolute main__cloud-2 fadeInPulse',
          },
          {
            file: 'effect-3.png',
            className: 'estop absolute main__effetct-3',
          },

          {
            file: 'effect-4.webp',
            className: 'estop absolute main__effetct-4 fadeInPulse',
          },
        ],
      },
      currency: 'currencyAz.svg',
    },
  },

  KG: {
    id: 'KG',
    phone: {
      code: 'KG',
      dial: '996',
      maxLength: 13,
      placeholder: '+996 XXX XXX XXX',
      flagSrc: getGeoAssetPath('KG', 'phone.svg'),
      flagAlt: 'KG flag',
    },
    layout: {
      mainElement1: {
        mobile: 'box-1.webp',
        tablet: 'box-12.webp',
        desktop: 'box-12.webp',
        className: 'main__box',
      },
      mainElement2: {
        mobile: 'girl-1.webp',
        tablet: 'girl-12.webp',
        desktop: 'girl-12.webp',
        className: 'main__girl-2',
      },
      decorElements: {
        mobile: [
          {
            file: 'chips.webp',
            className: 'estop absolute main__chips-3 flyingDown',
          },
        ],

        tablet: [
          {
            file: 'chips.webp',
            className: 'estop absolute main__chips-3 flyingDown',
          },

          {
            file: 'effect-2.webp',
            className: 'estop absolute main__effetct-2',
          },
        ],

        desktop: [
          {
            file: 'neon3.webp',
            className: 'estop main__neon fadeInPulse',
          },
          {
            file: 'effect-12.png',
            className: 'estop absolute main__effetct-1 fadeInPulse',
          },
          {
            file: 'effect-22.webp',
            className: 'estop absolute main__effetct-2 fadeInPulse',
          },

          {
            file: 'chips-03.webp',
            className: 'estop absolute main__chips-0 flyingDown',
          },

          {
            file: 'chips-13.webp',
            className: 'estop absolute main__chips-1 flyingDown',
          },

          {
            file: 'chips-23.webp',
            className: 'estop absolute main__chips-2 flyingDown',
          },

          {
            file: 'chips-33.webp',
            className: 'estop absolute main__chips-3 flyingDown',
          },

          {
            file: 'chips-4.webp',
            className: 'estop absolute main__chips-4 flyingDown',
          },

          {
            file: 'chips-5.webp',
            className: 'estop absolute main__chips-5 flyingDown',
          },

          {
            file: 'chips-6.webp',
            className: 'estop absolute main__chips-6 flyingDown',
          },

          {
            file: 'cloud-1.webp',
            className: 'estop absolute main__cloud-1 fadeInPulse',
          },
          {
            file: 'cloud-2.webp',
            className: 'estop absolute main__cloud-2 fadeInPulse',
          },
          {
            file: 'effect-3.png',
            className: 'estop absolute main__effetct-3',
          },

          {
            file: 'effect-4.webp',
            className: 'estop absolute main__effetct-4 fadeInPulse',
          },
        ],
      },
      currency: 'currencyKg.svg',
    },
  },

  KZ: {
    id: 'KZ',
    phone: {
      code: 'KZ',
      dial: '7',
      maxLength: 12,
      placeholder: '+7 XXX XXX XXXX',
      flagSrc: '/flags/kz.svg',
      flagAlt: 'KZ flag',
    },
    layout: {
      mainElement1: {
        mobile: 'girl-1.webp',
        tablet: 'girl-12.webp',
        desktop: 'girl-12.webp',
        className: 'main__girl-1',
      },
      mainElement2: {
        mobile: 'girl-2.webp',
        tablet: 'girl-22.webp',
        desktop: 'girl-22.webp',
        className: 'main__girl-2',
      },

      decorElements: {
        mobile: [
          {
            file: 'chips.webp',
            className: 'estop absolute main__chips-3 flyingDown',
          },
        ],

        tablet: [
          {
            file: 'chips.webp',
            className: 'estop absolute main__chips-3 flyingDown',
          },

          {
            file: 'effect-2.webp',
            className: 'estop absolute main__effetct-2',
          },
        ],

        desktop: [
          {
            file: 'neon3.webp',
            className: 'estop main__neon fadeInPulse',
          },
          {
            file: 'effect-12.png',
            className: 'estop absolute main__effetct-1 fadeInPulse',
          },
          {
            file: 'effect-22.webp',
            className: 'estop absolute main__effetct-2 fadeInPulse',
          },

          {
            file: 'chips-03.webp',
            className: 'estop absolute main__chips-0 flyingDown',
          },

          {
            file: 'chips-13.webp',
            className: 'estop absolute main__chips-1 flyingDown',
          },

          {
            file: 'chips-23.webp',
            className: 'estop absolute main__chips-2 flyingDown',
          },

          {
            file: 'chips-33.webp',
            className: 'estop absolute main__chips-3 flyingDown',
          },

          {
            file: 'chips-4.webp',
            className: 'estop absolute main__chips-4 flyingDown',
          },

          {
            file: 'chips-5.webp',
            className: 'estop absolute main__chips-5 flyingDown',
          },

          {
            file: 'chips-6.webp',
            className: 'estop absolute main__chips-6 flyingDown',
          },

          {
            file: 'cloud-1.webp',
            className: 'estop absolute main__cloud-1 fadeInPulse',
          },
          {
            file: 'cloud-2.webp',
            className: 'estop absolute main__cloud-2 fadeInPulse',
          },
          {
            file: 'effect-3.png',
            className: 'estop absolute main__effetct-3',
          },

          {
            file: 'effect-4.webp',
            className: 'estop absolute main__effetct-4 fadeInPulse',
          },
        ],
      },
      currency: 'currencyKz.svg',
    },
  },

  UZ: {
    id: 'UZ',
    phone: {
      code: 'UZ',
      dial: '998',
      maxLength: 13,
      placeholder: '+998 XX XXX XX XX',
      flagSrc: getGeoAssetPath('UZ', 'phone.svg'),
      flagAlt: 'UZ flag',
    },
    layout: {
      mainElement1: {
        mobile: 'box-1.webp',
        tablet: 'box-12.webp',
        desktop: 'box-12.webp',
        className: 'main__box',
      },
      mainElement2: {
        mobile: 'girl-1.webp',
        tablet: 'girl-12.webp',
        desktop: 'girl-12.webp',
        className: 'main__girl-2',
      },
      decorElements: {
        mobile: [
          {
            file: 'chips.webp',
            className: 'estop absolute main__chips-3 flyingDown',
          },
        ],

        tablet: [
          {
            file: 'chips.webp',
            className: 'estop absolute main__chips-3 flyingDown',
          },

          {
            file: 'effect-2.webp',
            className: 'estop absolute main__effetct-2',
          },
        ],

        desktop: [
          {
            file: 'neon3.webp',
            className: 'estop main__neon fadeInPulse',
          },
          {
            file: 'effect-12.png',
            className: 'estop absolute main__effetct-1 fadeInPulse',
          },
          {
            file: 'effect-22.webp',
            className: 'estop absolute main__effetct-2 fadeInPulse',
          },

          {
            file: 'chips-03.webp',
            className: 'estop absolute main__chips-0 flyingDown',
          },

          {
            file: 'chips-13.webp',
            className: 'estop absolute main__chips-1 flyingDown',
          },

          {
            file: 'chips-23.webp',
            className: 'estop absolute main__chips-2 flyingDown',
          },

          {
            file: 'chips-33.webp',
            className: 'estop absolute main__chips-3 flyingDown',
          },

          {
            file: 'chips-4.webp',
            className: 'estop absolute main__chips-4 flyingDown',
          },

          {
            file: 'chips-5.webp',
            className: 'estop absolute main__chips-5 flyingDown',
          },

          {
            file: 'chips-6.webp',
            className: 'estop absolute main__chips-6 flyingDown',
          },

          {
            file: 'cloud-1.webp',
            className: 'estop absolute main__cloud-1 fadeInPulse',
          },
          {
            file: 'cloud-2.webp',
            className: 'estop absolute main__cloud-2 fadeInPulse',
          },
          {
            file: 'effect-3.png',
            className: 'estop absolute main__effetct-3',
          },

          {
            file: 'effect-4.webp',
            className: 'estop absolute main__effetct-4 fadeInPulse',
          },
        ],
      },
      currency: 'currencyUz.svg',
    },
  },
}

export type ResolvedGeoState = {
  geo: SupportedGeo
  language: SupportedLanguage
  projectId: number | string | undefined
  baseCdaUrl: string | undefined
  allowedRegions: SupportedGeo[]
  allowedLanguages: SupportedLanguage[]
}

const getProjectInfoLanguages = (item: ProjectInfoEntry | undefined): SupportedLanguage[] => {
  if (!item) return []

  return Array.from(
    new Set(
      item.locales
        .map((locale) => normalizeLanguage(locale))
        .filter(Boolean) as SupportedLanguage[],
    ),
  )
}

export const getGeoRuntimeConfig = (
  projectInfo: ProjectInfoEntry[],
  geo: SupportedGeo,
): GeoRuntimeConfig => {
  const config = GEO_CONFIG[geo]
  const fallback = GEO_RUNTIME_FALLBACKS[geo]
  const infoByGeo = getProjectInfoByGeo(projectInfo, geo)
  const supportedLanguages = getProjectInfoLanguages(infoByGeo)

  return {
    ...config,
    supportedLanguages: supportedLanguages.length
      ? supportedLanguages
      : fallback.supportedLanguages,
    defaultLanguage: supportedLanguages[0] ?? fallback.defaultLanguage,
    countryOfResidence: infoByGeo?.geo_main_alpha_3[0] ?? fallback.countryOfResidence,
    currency: infoByGeo?.currencies[0] ?? fallback.currency,
  }
}

export const getResolvedLanguageForGeo = (
  geo: SupportedGeo,
  language: SupportedLanguage,
  projectInfo: ProjectInfoEntry[] = [],
) => {
  const runtimeConfig = getGeoRuntimeConfig(projectInfo, geo)

  return runtimeConfig.supportedLanguages.includes(language)
    ? language
    : runtimeConfig.defaultLanguage
}

export const getResolvedGeoStateForGeo = ({
  geo,
  language,
  projectInfo = [],
  allowedRegions,
  allowedLanguages,
}: {
  geo: SupportedGeo
  language: SupportedLanguage
  projectInfo?: ProjectInfoEntry[]
  allowedRegions: SupportedGeo[]
  allowedLanguages: SupportedLanguage[]
}): ResolvedGeoState => {
  const infoByGeo = getProjectInfoByGeo(projectInfo, geo)

  return {
    geo,
    language: getResolvedLanguageForGeo(geo, language, projectInfo),
    projectId: infoByGeo?.project_id,
    baseCdaUrl: infoByGeo?.cda,
    allowedRegions,
    allowedLanguages,
  }
}

const getAllowedRegions = (allowedRegions: Set<string>): SupportedGeo[] =>
  Array.from(allowedRegions)
    .map((region) => normalizeGeo(region))
    .filter((region): region is SupportedGeo => Boolean(region))

const getAllowedLanguages = (allowedLanguages: Set<string>): SupportedLanguage[] =>
  Array.from(allowedLanguages)
    .map((language) => normalizeLanguage(language))
    .filter((language): language is SupportedLanguage => Boolean(language))

const REGION_SETTINGS = {
  languageToRegion: {
    kz: 'KZ',
    az: 'AZ',
    tr: 'AZ',
    uz: 'UZ',
    kg: 'KG',
  },
  ianaToRegion: {
    'Asia/Almaty': { region: 'KZ', offsetHours: 5 },
    'Asia/Qyzylorda': { region: 'KZ', offsetHours: 5 },
    'Asia/Aqtau': { region: 'KZ', offsetHours: 5 },
    'Asia/Aqtobe': { region: 'KZ', offsetHours: 5 },
    'Asia/Atyrau': { region: 'KZ', offsetHours: 5 },
    'Asia/Oral': { region: 'KZ', offsetHours: 5 },
    'Asia/Baku': { region: 'AZ', offsetHours: 4 },
    'Asia/Tashkent': { region: 'UZ', offsetHours: 5 },
    'Asia/Samarkand': { region: 'UZ', offsetHours: 5 },
    'Asia/Bishkek': { region: 'KG', offsetHours: 6 },
  },
  utcOffsetTable: {
    4: 'AZ',
    6: 'KG',
  },
}

const filterRegionConfig = (
  regionConfig: Partial<Record<SupportedGeo, ProjectGeoConfig>>,
  allowedGeo: SupportedGeo[],
) => {
  if (!allowedGeo?.length) return regionConfig
  const filteredRegionConfig = Object.keys(regionConfig).reduce<
    Partial<Record<SupportedGeo, ProjectGeoConfig>>
  >((acc, region) => {
    if (allowedGeo.includes(region as SupportedGeo))
      acc[region as SupportedGeo] = regionConfig[region as SupportedGeo]
    return acc
  }, {})
  if (Object.keys(filteredRegionConfig).length) return filteredRegionConfig
  if (regionConfig[DEFAULT_GEO]) return { [DEFAULT_GEO]: regionConfig[DEFAULT_GEO] }

  return regionConfig
}

const buildRegionConfigFromProjectInfo = (projectInfo: ProjectInfoEntry[]) =>
  projectInfo.reduce<Partial<Record<SupportedGeo, ProjectGeoConfig>>>((acc, item) => {
    const region = item.geo_main[0]
    const currency = item.currencies[0]
    const languages = item.locales

    if (region && currency && languages.length) {
      acc[region as SupportedGeo] = {
        cda: item.cda,
        project_id: item.project_id,
        project_uuid: item.project_uuid,
        languages,
        currency,
      }
    }

    return acc
  }, {})

export const getProjectInfoByGeo = (projectInfo: ProjectInfoEntry[], geo: SupportedGeo) =>
  projectInfo.find((item) => normalizeGeo(item.geo_main[0]) === geo)

const getAllowedGeoQuery = (searchParams: URLSearchParams) => {
  const geoValues = searchParams.getAll(GEO_QUERY_KEY)

  return [
    ...new Set(
      geoValues
        .flatMap((value) => value.split(','))
        .map((value) => value.trim().toUpperCase())
        .filter((value): value is SupportedGeo => SUPPORTED_GEO.some((geo) => geo === value)),
    ),
  ]
}

export const resolveGeoState = ({
  search,
  storedGeo,
  storedLanguage,
  projectInfo,
  ipAlpha2,
}: {
  search: string
  storedGeo?: string | null
  storedLanguage?: string | null
  projectInfo: ProjectInfoEntry[]
  ipAlpha2?: string
}): ResolvedGeoState => {
  const allowedGeo = getAllowedGeoQuery(new URLSearchParams(search))
  const regionConfig = filterRegionConfig(buildRegionConfigFromProjectInfo(projectInfo), allowedGeo)

  // todo remove?
  if (!Object.keys(regionConfig).length) {
    // todo
  }

  const data = getRegionSettings(
    {
      ...REGION_SETTINGS,
      regionConfig,
    },
    {
      langSaved: normalizeLanguage(storedLanguage),
      regionSaved: normalizeGeo(storedGeo),
      navigatorLanguages:
        typeof navigator !== 'undefined'
          ? ([navigator.language, ...(navigator.languages ?? [])]
              .map((language) => normalizeLanguage(language))
              .filter(Boolean) as SupportedLanguage[])
          : [],
      utcOffsetHours:
        typeof window !== 'undefined' ? -new Date().getTimezoneOffset() / 60 : undefined,
      ianaTimeZone:
        typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined,
      ipAlpha2,
    },
  )

  const geo = normalizeGeo(data.region) ?? DEFAULT_GEO
  const geoRuntimeConfig = getGeoRuntimeConfig(projectInfo, geo)
  const language = getResolvedLanguageForGeo(
    geo,
    normalizeLanguage(data.language) ?? geoRuntimeConfig.defaultLanguage,
    projectInfo,
  )
  const allowedRegions = getAllowedRegions(data.allowedRegions)
  const allowedLanguages = getAllowedLanguages(data.allowedLang)

  return {
    geo,
    language,
    projectId: data.config?.project_id,
    baseCdaUrl: data.config?.cda,
    allowedRegions,
    allowedLanguages,
  }
}

declare global {
  interface Window {
    VARIANT_COPY_KEYS: typeof VARIANT_COPY_KEYS
  }
}

if (typeof window !== 'undefined') {
  window.VARIANT_COPY_KEYS = VARIANT_COPY_KEYS
}
